import React, { useEffect, useState } from 'react';

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

export default function RemoteEnrollPage() {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token') || '';
  const enrollType = urlParams.get('type') || (token.startsWith('EMP') ? 'employee' : 'member');

  const [info, setInfo] = useState<any>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'authenticating' | 'done' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [resolvedType, setResolvedType] = useState(enrollType);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg('No enrollment token provided in QR code.');
      return;
    }

    // Auto-detect endpoint: try employee first if type=employee, else try subscription
    const checkSession = async () => {
      try {
        let r = await fetch(`${BASE}/${enrollType === 'employee' ? 'employees' : 'subscriptions'}/remote-enroll/${token}`);
        let data = await r.json();

        if (!r.ok) {
          // Fallback to the other route
          const altType = enrollType === 'employee' ? 'subscriptions' : 'employees';
          r = await fetch(`${BASE}/${altType}/remote-enroll/${token}`);
          data = await r.json();
          if (r.ok) {
            setResolvedType(altType === 'employees' ? 'employee' : 'member');
          }
        }

        if (!r.ok) {
          throw new Error(data.detail || 'Enrollment session has expired. Please scan a fresh QR from desktop.');
        }

        setInfo(data);
        setStatus(data.status === 'completed' ? 'done' : 'ready');
      } catch (err: any) {
        setStatus('error');
        setErrorMsg(err.message || 'Session expired or invalid.');
      }
    };

    checkSession();
  }, [token, enrollType]);

  const handlePhoneTouchBiometric = async () => {
    setStatus('authenticating');
    try {
      if (!window.PublicKeyCredential) {
        throw new Error('WebAuthn biometrics not supported on this phone browser.');
      }

      const challenge = new Uint8Array(32);
      crypto.getRandomValues(challenge);
      const userId = new Uint8Array(16);
      crypto.getRandomValues(userId);

      const credential = await navigator.credentials.create({
        publicKey: {
          challenge,
          rp: { name: 'CloudAIRambo', id: window.location.hostname },
          user: {
            id: userId,
            name: (info?.name || 'User').toLowerCase().replace(/\s+/g, '_'),
            displayName: info?.name || 'User',
          },
          pubKeyCredParams: [
            { type: 'public-key', alg: -7 },   // ES256 (Android & iOS)
            { type: 'public-key', alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            authenticatorAttachment: 'platform',
            userVerification: 'required',
            residentKey: 'required',
          },
          timeout: 60000,
        },
      }) as PublicKeyCredential;

      if (!credential) throw new Error('Biometric check cancelled.');

      const rawId = btoa(String.fromCharCode(...new Uint8Array(credential.rawId)));
      const endpointPrefix = resolvedType === 'employee' ? 'employees' : 'subscriptions';

      const res = await fetch(`${BASE}/${endpointPrefix}/remote-enroll/${token}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential_id: rawId }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save biometric to server.');
      }

      setStatus('done');
    } catch (e: any) {
      setStatus('ready');
      alert(e.message || 'Biometric authentication was cancelled.');
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#070b14', display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: 20,
      fontFamily: "'DM Sans', system-ui, sans-serif", color: '#fff',
    }}>
      <div style={{
        background: '#0f172a', borderRadius: 28, padding: 32,
        width: '100%', maxWidth: 380, textAlign: 'center',
        border: '1px solid rgba(255,255,255,0.1)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
      }}>
        {status === 'loading' && (
          <div style={{ padding: 40, color: '#94a3b8', fontSize: 14 }}>Connecting to enrollment session...</div>
        )}

        {status === 'error' && (
          <div>
            <div style={{ fontSize: 56, marginBottom: 12 }}>❌</div>
            <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 8, color: '#f87171' }}>Session Expired</h2>
            <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>{errorMsg}</p>
          </div>
        )}

        {status === 'ready' && info && (
          <div>
            <div style={{
              width: 56, height: 56, borderRadius: 18, background: '#1e293b',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, margin: '0 auto 16px', border: '1px solid rgba(255,255,255,0.15)',
            }}>
              👆
            </div>
            <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 4 }}>Touch Fingerprint Sensor</h2>
            <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 24 }}>
              Registering biometric for <strong style={{ color: '#fff' }}>{info.name}</strong>
            </p>

            <button
              onClick={handlePhoneTouchBiometric}
              style={{
                width: '100%', padding: '16px', borderRadius: 14,
                background: 'linear-gradient(135deg, #10b981, #059669)',
                color: '#fff', border: 'none', fontSize: 15, fontWeight: 800,
                cursor: 'pointer', boxShadow: '0 4px 20px rgba(16,185,129,0.3)',
              }}
            >
              👆 Touch Fingerprint Sensor Now
            </button>
          </div>
        )}

        {status === 'authenticating' && (
          <div>
            <div style={{ fontSize: 56, marginBottom: 12, animation: 'pulse 1.5s infinite' }}>👆</div>
            <h2 style={{ fontSize: 18, fontWeight: 900, marginBottom: 8 }}>Waiting for Sensor...</h2>
            <p style={{ fontSize: 13, color: '#94a3b8' }}>Place your finger on the sensor or use Face ID.</p>
          </div>
        )}

        {status === 'done' && (
          <div>
            <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
            <h2 style={{ fontSize: 20, fontWeight: 900, color: '#34d399', marginBottom: 8 }}>Biometric Verified!</h2>
            <p style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.5 }}>
              The fingerprint has been linked to <strong style={{ color: '#fff' }}>{info?.name}</strong>.
            </p>
            <div style={{ marginTop: 20, fontSize: 12, color: '#64748b' }}>
              ✓ You can now close this tab. The desktop screen has been updated.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}